import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const QuickMessageComposer = ({ contact, onSendMessage, onClose }) => {
  const [message, setMessage] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [sendMethod, setSendMethod] = useState('email');
  const [isSending, setIsSending] = useState(false);

  const messageTemplates = [
    {
      id: 'checkin',
      title: 'Check-in',
      content: `Hi ${contact?.name}! Just wanted to check in and see how you're doing. Hope everything is going well!`,
      icon: 'MessageCircle'
    },
    {
      id: 'followup',title: 'Follow-up',
      content: `Hi ${contact?.name}, following up on our last conversation. Let me know if you need any additional information.`,
      icon: 'Reply'
    },
    {
      id: 'birthday',title: 'Birthday Wish',
      content: `Happy Birthday ${contact?.name}! 🎉 Hope you have a wonderful day filled with joy and celebration!`,
      icon: 'Gift'
    },
    {
      id: 'meeting',title: 'Meeting Request',
      content: `Hi ${contact?.name}, I'd love to schedule a meeting with you. Are you available sometime next week?`,
      icon: 'Calendar'
    },
    {
      id: 'thankyou',
      title: 'Thank You',
      content: `Thank you ${contact?.name} for your time and assistance. I really appreciate your help!`,
      icon: 'Heart'
    }
  ];

  const sendMethods = [
    { id: 'email', label: 'Email', icon: 'Mail', available: !!contact?.email },
    { id: 'sms', label: 'SMS', icon: 'MessageSquare', available: !!contact?.phone },
    { id: 'whatsapp', label: 'WhatsApp', icon: 'MessageCircle', available: !!contact?.phone }
  ];

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setMessage(template?.content);
  };

  const handleSend = async () => {
    if (!message?.trim()) return;

    setIsSending(true);
    try {
      await onSendMessage({
        message: message?.trim(),
        method: sendMethod,
        templateId: selectedTemplate?.id
      });
      
      // Reset form
      setMessage('');
      setSelectedTemplate(null);
      onClose();
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setIsSending(false);
    }
  };

  const characterCount = message?.length;
  const maxCharacters = sendMethod === 'sms' ? 160 : 500;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end lg:items-center justify-center z-300">
      <div className="bg-surface w-full lg:w-2xl lg:max-w-2xl lg:rounded-lg shadow-modal max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
              {contact?.avatar ? (
                <img 
                  src={contact?.avatar} 
                  alt={contact?.name}
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                <Icon name="User" size={20} className="text-text-secondary" />
              )}
            </div>
            <div>
              <h3 className="font-semibold text-text-primary">Send Message</h3>
              <p className="text-sm text-text-secondary">to {contact?.name}</p>
            </div>
          </div>
          
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="p-4 max-h-[calc(90vh-120px)] overflow-y-auto">
          {/* Send Method Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-text-primary mb-2">
              Send via
            </label>
            <div className="flex space-x-2">
              {sendMethods?.map((method) => (
                <button
                  key={method?.id}
                  onClick={() => setSendMethod(method?.id)}
                  disabled={!method?.available}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-micro ${
                    sendMethod === method?.id
                      ? 'bg-primary text-primary-foreground'
                      : method?.available
                        ? 'bg-muted text-text-secondary hover:text-text-primary hover:bg-muted/80' :'bg-muted/50 text-text-secondary/50 cursor-not-allowed'
                  }`}
                >
                  <Icon name={method?.icon} size={16} />
                  <span>{method?.label}</span>
                  {!method?.available && (
                    <Icon name="Lock" size={12} />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Message Templates */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-text-primary mb-2">
              Quick Templates
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {messageTemplates?.map((template) => (
                <button
                  key={template?.id}
                  onClick={() => handleTemplateSelect(template)}
                  className={`flex items-center space-x-3 p-3 rounded-lg text-left transition-micro ${
                    selectedTemplate?.id === template?.id
                      ? 'bg-primary/10 border border-primary/20 text-primary' :'bg-muted hover:bg-muted/80 text-text-secondary hover:text-text-primary'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    selectedTemplate?.id === template?.id
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-surface'
                  }`}>
                    <Icon name={template?.icon} size={16} />
                  </div>
                  <div>
                    <div className="font-medium">{template?.title}</div>
                    <div className="text-xs opacity-75 truncate">
                      {template?.content?.substring(0, 50)}...
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Message Composer */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-text-primary mb-2">
              Message
            </label>
            <div className="relative">
              <textarea
                value={message}
                onChange={(e) => setMessage(e?.target?.value)}
                placeholder={`Write a message to ${contact?.name}...`}
                className="w-full h-32 px-4 py-3 border border-border rounded-lg bg-surface text-text-primary placeholder-text-secondary resize-none focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                maxLength={maxCharacters}
              />
              <div className={`absolute bottom-3 right-3 text-xs ${
                characterCount > maxCharacters * 0.9 ? 'text-warning' : 'text-text-secondary'
              }`}>
                {characterCount}/{maxCharacters}
              </div>
            </div>
            
            {sendMethod === 'sms' && characterCount > 160 && (
              <p className="text-xs text-warning mt-1">
                Message will be sent as {Math.ceil(characterCount / 160)} SMS parts
              </p>
            )}
          </div>

          {/* Preview */}
          {message && (
            <div className="mb-4 p-3 bg-muted/50 rounded-lg border border-border">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="Eye" size={16} className="text-text-secondary" />
                <span className="text-sm font-medium text-text-primary">Preview</span>
              </div>
              <p className="text-sm text-text-primary leading-relaxed">
                {message}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-border bg-muted/30">
          <div className="flex items-center space-x-2 text-sm text-text-secondary">
            <Icon name={sendMethods?.find(m => m?.id === sendMethod)?.icon} size={16} />
            <span>
              Sending to: {
                sendMethod === 'email' ? contact?.email :
                sendMethod === 'sms' ? contact?.phone :
                sendMethod === 'whatsapp'? contact?.phone : 'Not available'
              }
            </span>
          </div>
          
          <div className="flex space-x-2">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isSending}
            >
              Cancel
            </Button>
            
            <Button
              variant="default"
              onClick={handleSend}
              loading={isSending}
              disabled={!message?.trim() || !sendMethods?.find(m => m?.id === sendMethod)?.available}
              iconName="Send"
              iconPosition="left"
            >
              Send Message
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickMessageComposer;